import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useUpdateVenueProfile } from "@/features/venues/model/useVenueProfile";
import type { VenueProfile } from "@/features/venues/types";
import { formatPhoneInput, nationalPhoneDigits } from "@/shared/lib/phone";

import { StepFooter } from "@/features/venue-onboarding/ui/StepFooter";

const schema = z.object({
  business_name: z.string().min(1, "Required").max(200),
  address: z.string().min(5, "Enter a full street address").max(500),
  contact_first_name: z.string().trim().min(1, "Required").max(100),
  contact_last_name: z.string().trim().min(1, "Required").max(100),
  contact_role: z.string().trim().max(100).optional(),
  contact_phone: z
    .string()
    .min(1, "Phone is required")
    .refine((v) => nationalPhoneDigits(v).length === 10, {
      message: "Enter a 10-digit phone number",
    }),
});

type Values = z.infer<typeof schema>;

interface Props {
  profile: VenueProfile;
  onSaved: () => void;
  onBack?: () => void;
}

export function BusinessStep({ profile, onSaved, onBack }: Props) {
  const update = useUpdateVenueProfile();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      business_name: profile.business_name ?? "",
      address: profile.address ?? "",
      contact_first_name: profile.contact_first_name ?? "",
      contact_last_name: profile.contact_last_name ?? "",
      contact_role: profile.contact_role ?? "",
      contact_phone: profile.contact_phone ?? "",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    await update.mutateAsync({
      business_name: values.business_name,
      address: values.address,
      contact_first_name: values.contact_first_name,
      contact_last_name: values.contact_last_name,
      contact_role: values.contact_role ?? "",
      contact_phone: `+1${nationalPhoneDigits(values.contact_phone)}`,
    });
    onSaved();
  });

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">
          Business basics
        </h2>
        <p className="mt-1 text-sm text-copy-muted">
          What workers will see on every shift card.
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor="business_name" className="text-sm font-medium text-copy">
          Business name
        </label>
        <input
          id="business_name"
          {...register("business_name")}
          placeholder="The Whalesbone"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring sm:text-sm"
        />
        {errors.business_name && (
          <p className="text-xs text-destructive">{errors.business_name.message}</p>
        )}
      </div>

      <div className="space-y-1">
        <label htmlFor="address" className="text-sm font-medium text-copy">
          Address
        </label>
        <input
          id="address"
          {...register("address")}
          placeholder="231 Bank St, Ottawa, ON"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring sm:text-sm"
        />
        {errors.address && (
          <p className="text-xs text-destructive">{errors.address.message}</p>
        )}
        <p className="text-xs text-copy-muted">
          Full street address — workers see the city and we use this to match nearby workers.
        </p>
      </div>

      <div className="space-y-3 rounded-md border border-border bg-surface-sunken p-4">
        <div>
          <p className="text-sm font-medium text-copy">Primary contact</p>
          <p className="mt-0.5 text-xs text-copy-muted">
            The person workers and our team should ask for. Doesn't have to be the account holder.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="contact_first_name" className="text-sm font-medium text-copy">
              First name
            </label>
            <input
              id="contact_first_name"
              {...register("contact_first_name")}
              placeholder="Barak"
              autoComplete="off"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring sm:text-sm"
            />
            {errors.contact_first_name && (
              <p className="text-xs text-destructive">{errors.contact_first_name.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <label htmlFor="contact_last_name" className="text-sm font-medium text-copy">
              Last name
            </label>
            <input
              id="contact_last_name"
              {...register("contact_last_name")}
              placeholder="Tov"
              autoComplete="off"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring sm:text-sm"
            />
            {errors.contact_last_name && (
              <p className="text-xs text-destructive">{errors.contact_last_name.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="contact_role" className="text-sm font-medium text-copy">
            Role <span className="text-copy-muted">(optional)</span>
          </label>
          <input
            id="contact_role"
            {...register("contact_role")}
            placeholder="Event Manager · GM · Owner"
            autoComplete="off"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring sm:text-sm"
          />
        </div>
      </div>

      <div className="space-y-1">
        <label htmlFor="contact_phone" className="text-sm font-medium text-copy">
          Contact phone
        </label>
        <Controller
          control={control}
          name="contact_phone"
          render={({ field }) => (
            <input
              id="contact_phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={field.value}
              onChange={(e) => field.onChange(formatPhoneInput(e.target.value))}
              onBlur={field.onBlur}
              placeholder="+1 (613) 555-1234"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring sm:text-sm"
            />
          )}
        />
        {errors.contact_phone && (
          <p className="text-xs text-destructive">{errors.contact_phone.message}</p>
        )}
        <p className="text-xs text-copy-muted">
          Goes to confirmed workers if they need to reach you on the day of.
        </p>
      </div>

      <StepFooter
        onBack={onBack}
        isSubmitting={isSubmitting || update.isPending}
        nextLabel="Continue"
      />
    </form>
  );
}
